import type { ReactNode } from 'react';
import styles from './BarSeries.module.css';

/**
 * ============================== BAR SERISI ==================================
 *
 * Kucuk, bagimliliksiz bir yatay bar serisi. Panel ve rapor ekranlarinin
 * ORTAK bileseni — ikisi de `viewReport` ile korunan rapor yuzeyleri ve ayni
 * kirilim gorselini kullaniyor, o yuzden ayni klasorde tek kopya duruyor
 * (`src/features/dashboard` bunu buradan import eder).
 *
 * ---------------------- NEDEN KUTUPHANE YOK --------------------------------
 * Ihtiyac duyulan sey bir eksen, tooltip ve olcekleme motoru degil: tek boyutlu
 * bir oran karsilastirmasi. Bir grafik kutuphanesi (recharts/visx) bunun icin
 * yuz kilobaytlarca JS ve kendi tema sistemini getirirdi. Kutuphane secimi ayri
 * bir karar; once hafif port.
 *
 * ------------------------- ERISILEBILIRLIK ---------------------------------
 * Cizim SALT GORSEL bir katman: `<svg aria-hidden="true">`. Verinin kendisi
 * gercek bir `<table>` olarak basiliyor — etiket, deger ve (varsa) yardimci
 * metin ekran okuyucuya duz metin olarak ulasiyor. "aria-label'a sikistirilmis
 * bir resim" yerine tablo secildi cunku sayilar zaten okunmak isteniyor;
 * gormeyen kullanici ozet degil AYNI veriyi aliyor.
 *
 * Renk TEK BASINA anlam tasimaz (WCAG 1.4.1): her barin degeri kendi satirinda
 * yazili. Renkler yalniz --color-chart-series-* jetonlarindan geliyor, ham
 * renk yok (stylelint color-no-hex bunu zorluyor).
 * ========================================================================== */

export type BarDatum = Readonly<{
  /** Kararli anahtar — para birimi kodu, durum kodu, kullanici kimligi… */
  id: string;
  /** CEVRILMIS etiket. Modul sabitlerinde `labelKey` saklanir, burada t()'li gelir. */
  label: ReactNode;
  /** Oranlarin hesaplandigi HAM sayi. Negatif beklenmiyor. */
  value: number;
  /** Ekrana basilacak bicimli deger (<MoneyText> gecirilebilir). */
  display: ReactNode;
  /** Satirin altindaki ikincil metin — "1397/2711 kazanıldı" gibi. */
  hint?: ReactNode;
}>;

export type BarSeriesProps = {
  data: readonly BarDatum[];
  /** Tablonun basligi — ekran okuyucu icin ZORUNLU baglam. */
  caption: string;
  /** Deger sutununun basligi ("Kur toplamı", "Hacim"…). */
  valueLabel: string;
  /** Etiket sutununun basligi ("Para birimi", "Durum"…). */
  labelHeading: string;
};

/** Jeton sayisi kadar seri rengi; sirayla dagitilir. */
const SERIES_COUNT = 5;

export function BarSeries({ data, caption, valueLabel, labelHeading }: BarSeriesProps) {
  // Payda ASLA 0 olmaz: tum degerler sifirsa her bar en kucuk genislige duser
  // (bolme sonucu NaN olsaydi SVG genisligi sessizce cizilmezdi).
  const max = data.reduce((acc, item) => (item.value > acc ? item.value : acc), 0) || 1;

  return (
    <table className={styles.table}>
      <caption className={styles.caption}>{caption}</caption>
      <thead>
        {/* Basliklar gorsel olarak gizli: kirilim listesinde bir baslik satiri
            gurultu yapardi, ama ekran okuyucunun sutunlari adlandirmasi sart. */}
        <tr>
          <th scope="col" className={styles.srOnly}>
            {labelHeading}
          </th>
          <th scope="col" className={styles.srOnly}>
            {valueLabel}
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => {
          // En az %2: sifira yakin bir deger de bir iz birakmali, yoksa satir
          // "veri yok" gibi gorunur.
          const ratio = Math.max(Math.min(item.value / max, 1), 0.02);
          const seriesIndex = (index % SERIES_COUNT) + 1;

          return (
            <tr key={item.id} className={styles.row}>
              <th scope="row" className={styles.cellLabel}>
                <span className={styles.label}>{item.label}</span>
                {item.hint === undefined ? null : (
                  <span className={styles.hint}>{item.hint}</span>
                )}
              </th>
              <td className={styles.cellValue}>
                <span className={styles.value}>{item.display}</span>
                {/* SALT GORSEL: veri yukaridaki hucrelerde zaten duz metin. */}
                <svg
                  className={styles.chart}
                  viewBox="0 0 100 6"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <rect className={styles.track} x="0" y="0" width="100" height="6" rx="3" />
                  <rect
                    className={styles.fill}
                    data-series={seriesIndex}
                    x="0"
                    y="0"
                    width={ratio * 100}
                    height="6"
                    rx="3"
                  />
                </svg>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
